import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import "./TermsofService.css";
import GlobalNavDrawer from "../components/GlobalNavDrawer";
import { TokenContext } from "./tokenContext"; // import TokenContext
import { logout } from "../utils/firebase";

function TermsofService() {
  const { balance } = useContext(TokenContext);
  const lastUpdated = "September 26, 2025";
  
  const navigate = useNavigate();
  const navigateTo = (path) => {
    navigate(path);
  };
  
  return (
    <main className="TOS" role="main" id="top">
      {/* Background glows (non-interactive) */}
      <div className="tosGlow tosGlow--a" aria-hidden="true" />
      <div className="tosGlow tosGlow--b" aria-hidden="true" />

      {/* Page header */}
      <header className="tosHeader">
        <h1 className="tosTitle">Terms of Service</h1>
        <p className="tosStamp">The iSpeakWell Project · Last updated {lastUpdated}</p>
      </header>

      {/* Layout: sticky ToC + content */}
      <div className="tosLayout">
        <aside className="tosToc" aria-label="Table of contents">
          <nav>
            <ol>
              <li><a href="#acknowledgment">Acknowledgment</a></li>
              <li><a href="#interpretation-definitions">Interpretation & Definitions</a></li>
              <li><a href="#accounts">User Accounts</a></li>
              <li><a href="#content-rights">Content & Your Rights</a></li>
              <li><a href="#content-restrictions">Content Restrictions</a></li>
              <li><a href="#backups">Content Backups</a></li>
              <li><a href="#copyright">Copyright / DMCA</a></li>
              <li><a href="#products-orders">Products & Orders</a></li>
              <li><a href="#prices-payments">Prices & Payments</a></li>
              <li><a href="#promotions">Promotions</a></li>
              <li><a href="#ip">Intellectual Property</a></li>
              <li><a href="#podcast-participation">Live Podcast Participation & Consent</a></li>
              <li><a href="#feedback">Your Feedback to Us</a></li>
              <li><a href="#links">Links to Other Websites</a></li>
              <li><a href="#termination">Termination</a></li>
              <li><a href="#liability">Limitation of Liability</a></li>
              <li><a href="#asis">“AS IS” & “AS AVAILABLE”</a></li>
              <li><a href="#law">Governing Law & Disputes</a></li>
              <li><a href="#compliance">EU & U.S. Compliance</a></li>
              <li><a href="#severability-waiver">Severability & Waiver</a></li>
              <li><a href="#changes">Changes to These Terms</a></li>
              <li><a href="#tokens">Virtual Currency (Tokens)</a></li>
              <li><a href="#refunds">Refunds & Cancellations (Cash Purchases)</a></li>
              <li><a href="#how-to-request">How to Request</a></li>
              <li><a href="#contact">Contact Us</a></li>
            </ol>
          </nav>
        </aside>

        <article className="tosContent">

          {/* Acknowledgment */}
          <section id="acknowledgment" className="tosCard">
            <h2>Acknowledgment</h2>
            <p>
              These Terms govern your use of the Service and form the agreement between you and Language Play Studio (“Company”).
              Accessing or using the Service means you agree to these Terms. If you do not agree, do not use the Service.
            </p>
            <p>
              You must be at least 18 years old to use the Service. Use is also conditioned on acceptance of our Privacy Policy.
            </p>
          </section>

          {/* Interpretation & Definitions */}
          <section id="interpretation-definitions" className="tosCard">
            <h2>Interpretation &amp; Definitions</h2>
            <h3>Interpretation</h3>
            <p>
              Capitalized words have meanings defined below; definitions apply equally to singular and plural.
            </p>
            <h3>Definitions</h3>
            <ul>
              <li><strong>Affiliate</strong>: Entity controlling, controlled by, or under common control with a party (≥50% ownership).</li>
              <li><strong>Account</strong>: Your unique account for accessing the Service.</li>
              <li><strong>Company</strong>: Language Play Studio (“we”, “us”, “our”).</li>
              <li><strong>Country</strong>: Canada.</li>
              <li><strong>Content</strong>: Text, images, or other material posted or made available by you.</li>
              <li><strong>Device</strong>: Any device capable of accessing the Service.</li>
              <li><strong>Feedback</strong>: Feedback, innovations, or suggestions sent by you.</li>
              <li><strong>Products</strong>: Items offered for sale on the Service.</li>
              <li><strong>Orders</strong>: Requests to purchase Products.</li>
              <li><strong>Promotions</strong>: Contests, sweepstakes, or other promotional offers.</li>
              <li><strong>Service</strong>: The iSpeakWell website and related services.</li>
              <li><strong>Terms</strong>: These Terms of Service.</li>
              <li><strong>Third-party Social Media Service</strong>: Services or content provided by third parties.</li>
              <li><strong>Website</strong>: iSpeakWell.ca.</li>
              <li><strong>You</strong>: The individual or entity using the Service.</li>
            </ul>
          </section>

          {/* Accounts */}
          <section id="accounts" className="tosCard">
            <h2>User Accounts</h2>
            <p>
              Provide accurate, complete, and current information. You are responsible for safeguarding your password and for any
              activity under your account. Do not use a username that infringes others’ rights or is offensive.
            </p>
          </section>

          {/* Content rights */}
          <section id="content-rights" className="tosCard">
            <h2>Content &amp; Your Rights</h2>
            <p>
              By posting Content you grant us a license to use, modify, perform, display, reproduce, and distribute it through the Service.
              You retain your rights to your Content and are responsible for protecting them.
            </p>
            <p>
              You represent and warrant that you own or have rights to post the Content and that posting does not violate others’ rights.
            </p>
          </section>

          {/* Content restrictions */}
          <section id="content-restrictions" className="tosCard">
            <h2>Content Restrictions</h2>
            <p>
              You are solely responsible for your Content and all activity under your account. You may not post unlawful, defamatory,
              discriminatory, obscene, spam, malicious code, infringing material, impersonations, privacy violations, or false information.
              We may remove or edit Content and limit use of the Service for violations.
            </p>
          </section>

          {/* Backups */}
          <section id="backups" className="tosCard">
            <h2>Content Backups</h2>
            <p>
              While we perform regular backups, we do not guarantee against data loss or corruption. Maintain independent copies of your Content.
            </p>
          </section>

          {/* Copyright / DMCA */}
          <section id="copyright" className="tosCard">
            <h2>Copyright Policy &amp; DMCA</h2>
            <h3>Intellectual Property Infringement</h3>
            <p>
              We respect IP rights and respond to claims of infringement occurring through the Service. Email our copyright agent with details
              of the alleged infringement.
            </p>
            <h3>DMCA Notice</h3>
            <ul>
              <li>Signature of the rights owner or authorized agent;</li>
              <li>Description of the copyrighted work;</li>
              <li>Location (URL) of infringing material on the Service;</li>
              <li>Your address, phone, and email;</li>
              <li>Good-faith statement that the use is unauthorized;</li>
              <li>Statement under penalty of perjury that your notice is accurate and you are authorized.</li>
            </ul>
          </section>

          {/* Products & Orders */}
          <section id="products-orders" className="tosCard">
            <h2>Placing Orders for Products</h2>
            <p>
              By placing an Order you warrant you’re legally capable of entering binding contracts. We may ask for your name, email, phone,
              payment details, billing and shipping information, and you represent you’re authorized to use the payment method.
            </p>
            <h3>Order Cancellation</h3>
            <p>
              We may refuse or cancel Orders for reasons including product availability, errors in description or pricing, or suspected fraud/illegal activity.
            </p>
            <h3>Your Order Cancellation Rights</h3>
            <p>
              Returns/refunds for Products are handled under these Terms and applicable law (see also Refunds & Cancellations below).
            </p>
            <h3>Availability, Errors &amp; Inaccuracies</h3>
            <p>
              Offerings may be mispriced, inaccurate, or unavailable. We may update/correct information without prior notice.
            </p>
          </section>

          {/* Prices & Payments */}
          <section id="prices-payments" className="tosCard">
            <h2>Prices &amp; Payments</h2>
            <p>
              We may revise prices any time prior to accepting an Order and, in certain circumstances beyond our control post-acceptance.
              Payment cards are subject to issuer authorization; lack of authorization may delay or prevent delivery.
            </p>
          </section>

          {/* Promotions */}
          <section id="promotions" className="tosCard">
            <h2>Promotions</h2>
            <p>
              Promotions may have separate rules. If there’s a conflict, Promotion rules control for that Promotion.
            </p>
          </section>

          {/* IP */}
          <section id="ip" className="tosCard">
            <h2>Intellectual Property</h2>
            <p>
              The Service (excluding user Content) is the exclusive property of the Company and its licensors and protected by applicable laws.
              Our marks and trade dress may not be used without prior written consent.
            </p>
          </section>
          {/* Live Podcast Participation */}
          <section id="podcast-participation" className="tosCard">
            <h2>Live Podcast Participation & Consent</h2>
            <p>
              If you join a room labeled “Podcast,” you consent to live audio/video recording and to our analysis of your speech,
              including identification of challenges and constructive verbal/vocal feedback. You grant the Company a non-exclusive,
              worldwide, royalty-free license to use, reproduce, edit, publicly perform, display, and distribute your participation,
              in whole or in part, in connection with <em>The iSpeakWell Project</em> and <em>The iSpeakWell Podcast</em>.
            </p>
            <p>
              Recordings may be broadcast or rebroadcast on our websites and channels (including YouTube) and may appear on third-party
              platforms, apps, radios, televisions, PCs, and mobile devices now known or later developed.
            </p>
            <p>
              Our intention is to help learners build confidence and fluency—not to humiliate, embarrass, or demean any person, group,
              nationality, custom, religion, or accent. Participation is voluntary. If you do not accept, you must not join the Podcast room.
            </p>
            <h3>Public Exposure & Indemnity</h3>
            <p>
              Public participation entails risks, including possible recognition or doxxing by individuals not affiliated with us.
              You acknowledge these risks and agree that the Company, its employees, and platforms are not liable for actions of third parties.
              To the maximum extent permitted by law, you agree to indemnify and hold us harmless from claims arising from your voluntary participation.
            </p>
            <p>
              You may withdraw consent for future recordings at any time via <a href="mailto:privacy@ispeakwell.ca">privacy@ispeakwell.ca</a>.
              Previously published materials may continue to appear as allowed by law and platform policies.
            </p>
          </section>
          {/* Feedback */}
          <section id="feedback" className="tosCard">
            <h2>Your Feedback to Us</h2>
            <p>
              You assign all rights in Feedback to the Company; if assignment is ineffective, you grant a perpetual, irrevocable, worldwide,
              royalty-free license to use and exploit the Feedback.
            </p>
          </section>

          {/* Links */}
          <section id="links" className="tosCard">
            <h2>Links to Other Websites</h2>
            <p>
              Third-party sites/services are not owned or controlled by us. We’re not responsible for their content, policies, or practices.
              Review their terms and privacy policies.
            </p>
          </section>

          {/* Termination */}
          <section id="termination" className="tosCard">
            <h2>Termination</h2>
            <p>
              We may suspend or terminate your Account immediately for any breach of these Terms. Upon termination, your right to use the Service ceases.
            </p>
          </section>

          {/* Liability */}
          <section id="liability" className="tosCard">
            <h2>Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, the Company and suppliers will not be liable for special, incidental, indirect, or consequential damages.
              In any case, our aggregate liability is limited to the amount you paid via the Service or USD 100 if you haven’t purchased anything.
            </p>
          </section>

          {/* AS IS */}
          <section id="asis" className="tosCard">
            <h2>“AS IS” and “AS AVAILABLE”</h2>
            <p>
              The Service is provided without warranties of any kind, express or implied, including merchantability, fitness for a particular purpose,
              title, non-infringement, uninterrupted or error-free operation, or freedom from harmful components.
            </p>
          </section>

          {/* Law & Disputes */}
          <section id="law" className="tosCard">
            <h2>Governing Law &amp; Disputes</h2>
            <p>
              These Terms are governed by the laws of Canada (conflict-of-laws excluded). If you have a dispute, you agree to attempt informal resolution first.
            </p>
          </section>

          {/* Compliance */}
          <section id="compliance" className="tosCard">
            <h2>EU &amp; U.S. Compliance</h2>
            <p>
              EU consumers benefit from any mandatory provisions of their country of residence. You also represent you are not located in an embargoed country
              and are not on any U.S. restricted party list.
            </p>
          </section>

          {/* Severability & Waiver */}
          <section id="severability-waiver" className="tosCard">
            <h2>Severability &amp; Waiver</h2>
            <p>
              If any provision is unenforceable, it will be modified to achieve its objectives to the extent possible; the remainder stays in effect.
              Failure to enforce a right is not a waiver of future enforcement.
            </p>
          </section>

          {/* Changes */}
          <section id="changes" className="tosCard">
            <h2>Changes to These Terms</h2>
            <p>
              We may modify these Terms at our discretion. Material changes: we’ll make reasonable efforts to give at least 30 days’ notice before they take effect.
              Continuing to use the Service after changes are effective means you accept the new Terms.
            </p>
          </section>

          {/* TOKENS & REFUNDS — from mold, unified and expanded */}
          <section id="tokens" className="tosCard">
            <h2>Virtual Currency (Tokens)</h2>

            <h3>Nature of Tokens</h3>
            <p>
              Tokens are prepaid, non-transferable credits used solely to access iSpeakWell features. Tokens have no cash value and are not redeemable for cash.
            </p>

            <h3>Token Purchases (Cash → Tokens)</h3>
            <ul>
              <li>Token purchases may be eligible for a refund within <strong>7 days</strong> if <em>unused</em>.</li>
              <li>If any tokens are consumed, we may offer account credit or prorated value at our discretion.</li>
              <li><strong>Payment processing fees are non-refundable.</strong></li>
            </ul>

            <h3>Token Redemptions (Tokens → Services)</h3>
            <ul>
              <li>Token redemptions are <strong>final once a service begins</strong>.</li>
              <li>We automatically return tokens for services that cannot be delivered (e.g., system failure before start).</li>
              <li>If a service is partially delivered due to our fault, we may issue a partial token return.</li>
            </ul>

            <h3>No Cash for Token Spends</h3>
            <p>Refunds for token redemptions, if any, are in <strong>tokens or account credit only</strong>, not cash.</p>

            <h3>Abuse &amp; Fraud</h3>
            <p>We may deny refunds/returns for suspected abuse, excessive reversals, or violations of these Terms.</p>
          </section>

          <section id="refunds" className="tosCard">
            <h2>Refunds &amp; Cancellations (Cash Purchases)</h2>

            <h3>Eligibility Window</h3>
            <p>Cash purchases (plans or token packs) are refundable within <strong>7 days</strong> if unused.</p>

            <h3>Partial Use (Days 8–30)</h3>
            <p>We may issue a prorated refund or credit for unused time/benefits; processing fees are excluded from cash refunds.</p>

            <h3>After 30 Days</h3>
            <p>No cash refunds; account credit may be provided for verified technical failures.</p>
          </section>

          <section id="how-to-request" className="tosCard">
            <h2>How to Request</h2>
            <p>
              Email <a className="PRV-LINK" href="mailto:support@ispeakwell.ca">support@ispeakwell.ca</a> with your order ID. We may review usage logs to determine eligibility.
            </p>
          </section>

          {/* Contact */}
          <section id="contact" className="tosCard tosCard--muted">
            <h2>Contact Us</h2>
            <p>
              If you have questions about these Terms, contact
              {" "}
              <a className="PRV-LINK" href="mailto:info@ispeakwell.ca">info@ispeakwell.ca</a>.
            </p>
            <p className="tosNote">This document summarizes key legal terms for your convenience and is not legal advice.</p>
          </section>

          <footer className="tosFooter">
            <a
              className="tosTop"
              href="#top"
              onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            >
              ▲ Back to top
            </a>
          </footer>
        </article>
      </div>
      <GlobalNavDrawer
        hideCurrent
        links={[
          { to: "/purchase", label: `Tokens: ${balance}`},
          { to: "/voicebot", label: "iSpeakWell" },
          { to: "/resumerevisor", label: "Resumes" },
          { to: "/conversationpractice", label: "Convos" },
          { to: "/interview-practice", label: "Interviews" },
          { to: "/privacypolicy", label: "Privacy" },
          { to: "/admin", label: "Admin" },
          { to: "/login", label: "Logout", onClick: async () => { await logout(); } },
        ]}
      />
      <div className="TOS-links-section">
        <button
          className="TOS-link-button"
          onClick={() => navigateTo("/about")}
        >
          About
        </button>
        <button
          className="TOS-link-button"
          onClick={() => navigateTo("/careers")}
        >
          Careers
        </button>
        <button
          className="TOS-link-button"
          onClick={() => navigateTo("/tokens")}
        >
          Tokens
        </button>
        <button
          className="TOS-link-button"
          onClick={() => navigateTo("/contactus")}
        >
          Contact Us
        </button>
        <button
          className="TOS-link-button"
          onClick={() => navigateTo("/login")}
        >
          Login
        </button>
      </div>
    </main>
  );
}

export default TermsofService;
